import { useId, type ComponentType } from 'react';
import { Check } from 'lucide-react';

/**
 * Большая карточка-радио для бинарных/тернарных выборов в анкетах калькулятора
 * (Block 12 PR-B Phase B1). Generalization паттерна `TypeCard` из
 * `apps/web/src/features/calculator/CalculatorEntryView.tsx` — но как
 * полноценная radio-group с fieldset/legend, hint/error и иконками.
 *
 * Используется для: «Прямая / Угловая» кухня, «Открытый / Встроенный» холодильник,
 * «С ручками / От нажатия / Гола» фасад и т.п. Вертикальный список (P-019).
 *
 * Поведение:
 * - Wraps `<fieldset>` + `<legend>` — semantic radio group.
 * - Каждая опция — `<label>` обёртка `<input type="radio" className="sr-only">` (visual
 *   click target; sr-only вместо `hidden` чтобы keyboard focus работал).
 * - Visual states: default (border-accent), checked (border-cta + bg-cta/10 + Check icon
 *   справа), error+checked (border-error), hover (bg-accent/5).
 * - Keyboard nav: arrow up/down — нативное поведение radio group (browser).
 * - Hint / Error pattern идентичен TextInput/Select.
 *
 * Required prop рендерит звёздочку `*` после legend (aria-hidden). НЕ выполняет
 * валидацию — это работа Container'а (Zod в Phase B3 AnketaForm).
 */
export interface RadioCardOption {
  value: string;
  label: string;
  /** Optional description под label. */
  description?: string;
  /** Optional иконка (lucide-react component) слева от content. */
  icon?: ComponentType<{ size?: number; className?: string }>;
}

export interface RadioCardGroupProps {
  /** name атрибут на всех input[type=radio] — связывает radio в group. */
  name: string;
  /** Заголовок группы (legend). Например «Тип кухни». */
  label: string;
  options: ReadonlyArray<RadioCardOption>;
  /** Текущее значение (null = ничего не выбрано). */
  value: string | null;
  onChange: (value: string) => void;
  /** Подсказка под legend (text-muted text-sm). */
  hint?: string;
  /** Сообщение об ошибке — overrides hint, role=alert. */
  error?: string;
  /** Если true — звёздочка `*` после legend (aria-hidden). */
  required?: boolean;
}

export function RadioCard({
  name,
  label,
  options,
  value,
  onChange,
  hint,
  error,
  required,
}: RadioCardGroupProps) {
  const reactId = useId();
  const hintId = `${name}-${reactId}-hint`;
  const errorId = `${name}-${reactId}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;
  const hasError = Boolean(error);

  return (
    <fieldset
      className="flex flex-col"
      aria-describedby={describedBy}
      aria-invalid={hasError || undefined}
    >
      <legend className="text-ink font-semibold text-base mb-2">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-error ml-1">
            *
          </span>
        ) : null}
      </legend>
      {hint && !hasError ? (
        <p id={hintId} className="text-muted text-sm -mt-1 mb-2">
          {hint}
        </p>
      ) : null}
      <div className="flex flex-col gap-3">
        {options.map((opt) => {
          const checked = value === opt.value;
          const Icon = opt.icon;
          const borderClass =
            hasError && checked
              ? 'border-error bg-error/5'
              : checked
                ? 'border-cta bg-cta/10'
                : 'border-accent bg-card';
          return (
            <label
              key={opt.value}
              className={
                'min-h-tap rounded-xl border-[1.5px] px-4 py-3 flex items-center gap-3 ' +
                'cursor-pointer focus-within:ring-2 focus-within:ring-cta ' +
                'hover:bg-accent/5 active:opacity-90 ' +
                borderClass
              }
            >
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={checked}
                onChange={() => onChange(opt.value)}
                className="sr-only"
              />
              {Icon ? (
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 w-10 h-10 rounded-full bg-icon-bg flex items-center justify-center"
                >
                  <Icon size={22} className={checked ? 'text-cta' : 'text-muted'} />
                </span>
              ) : null}
              <span className="flex-1 text-left">
                <span className="block text-base font-semibold text-ink leading-tight">
                  {opt.label}
                </span>
                {opt.description ? (
                  <span className="block text-sm text-muted mt-0.5">{opt.description}</span>
                ) : null}
              </span>
              {checked ? (
                <Check size={20} aria-hidden="true" className="text-cta flex-shrink-0" />
              ) : null}
            </label>
          );
        })}
      </div>
      {hasError ? (
        <p id={errorId} role="alert" className="text-error text-sm mt-2">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
